// Pure point sampler for the field. No THREE, no DOM.
//
// Given a grid, the per-component data arrays, and a scalar array (typically
// magnitude or the currently-selected component), returns the sampled value,
// the vector components, and the {x,y,z} vector form when Bx/By/Bz exist.
//
// Out-of-bounds points return zeros — callers should check `inBounds` if
// they need to distinguish from a legitimately-zero sample.

import { isOutsideBounds, trilinear } from './sample';
import type {
  FieldComponents,
  PhysicsGrid,
  ProbeResult,
  ScalarField,
  Vec3,
} from './types';

// Local view of the viewer-context grid + field slices. The full viewer type
// is owned by PR 7/8; this narrow shape is what `getFieldValue` actually reads.
interface ViewerGridSlice {
  nx: number;
  ny: number;
  nz: number;
  xMin: number;
  yMin: number;
  zMin: number;
  resolutionX: number;
  resolutionY: number;
  resolutionZ: number;
}

interface ViewerFieldSlice {
  currentComponent: string;
  gridFlat: Float32Array | null;
  data: Record<string, Float32Array | undefined> | null;
}

interface ViewerWithFieldGrid {
  grid: ViewerGridSlice;
  field: ViewerFieldSlice;
}

export function probe(
  grid: PhysicsGrid | null,
  components: FieldComponents | null,
  scalar: ScalarField | null,
  pos: Vec3,
): ProbeResult {
  const [x, y, z] = pos;
  if (!grid || isOutsideBounds(grid, x, y, z)) {
    return { value: 0, vec: null, components: {}, inBounds: false };
  }
  const value = scalar ? trilinear(scalar, grid, x, y, z) : 0;
  const out: Record<string, number> = {};
  if (components) {
    for (const name of Object.keys(components)) {
      const arr = components[name];
      if (arr && arr.length) out[name] = trilinear(arr, grid, x, y, z);
    }
  }
  const vec = (out['Bx'] !== undefined && out['By'] !== undefined && out['Bz'] !== undefined)
    ? { x: out['Bx'], y: out['By'], z: out['Bz'] }
    : null;
  return { value, vec, components: out, inBounds: true };
}

export function makeProbe(
  grid: PhysicsGrid | null,
  components: FieldComponents | null,
  scalar: ScalarField | null,
): (pos: Vec3) => ProbeResult {
  return (pos) => probe(grid, components, scalar, pos);
}

/**
 * Nearest-neighbour scalar sampler. Cheap (single Float32Array read, no
 * interpolation) — used for high-throughput rasterisation like slice
 * canvases. Out-of-bounds returns 0.
 */
export function sampleNearest(
  grid: PhysicsGrid,
  scalar: ScalarField | null,
  x: number,
  y: number,
  z: number,
): number {
  if (!scalar) return 0;
  const i = Math.floor((x - grid.xmin) / grid.dx);
  const j = Math.floor((y - grid.ymin) / grid.dy);
  const k = Math.floor((z - grid.zmin) / grid.dz);
  if (i < 0 || i >= grid.nx || j < 0 || j >= grid.ny || k < 0 || k >= grid.nz) return 0;
  return scalar[i * (grid.ny * grid.nz) + j * grid.nz + k]!;
}

// Per-viewer physics-grid view cache. Hoisted out of the hot path so slice
// rasterisation (~67k samples per refresh) doesn't allocate a fresh literal
// per pixel. The cache key is the viewer reference; the cached object is
// only rebuilt when the dataset grid changes.
const _gridViewCache = new WeakMap<object, PhysicsGrid>();

function physicsGridView(viewer: ViewerWithFieldGrid): PhysicsGrid {
  const g = viewer.grid;
  const cached = _gridViewCache.get(viewer);
  if (cached
      && cached.nx === g.nx && cached.ny === g.ny && cached.nz === g.nz
      && cached.xmin === g.xMin && cached.ymin === g.yMin && cached.zmin === g.zMin
      && cached.dx === g.resolutionX) return cached;
  const v: PhysicsGrid = {
    nx: g.nx, ny: g.ny, nz: g.nz,
    xmin: g.xMin, ymin: g.yMin, zmin: g.zMin,
    xmax: g.xMin + (g.nx - 1) * g.resolutionX,
    ymax: g.yMin + (g.ny - 1) * g.resolutionY,
    zmax: g.zMin + (g.nz - 1) * g.resolutionZ,
    dx: g.resolutionX, dy: g.resolutionY, dz: g.resolutionZ,
  };
  _gridViewCache.set(viewer, v);
  return v;
}

export function getFieldValue(viewer: ViewerWithFieldGrid, x: number, y: number, z: number): number {
  const f = viewer.field;
  const arr = (f.currentComponent === 'magnitude') ? f.gridFlat : (f.data && f.data[f.currentComponent]);
  return arr ? sampleNearest(physicsGridView(viewer), arr, x, y, z) : 0;
}
